import RESPONSE_HANDLER, { REPLY_TYPE } from './responseHandler';
import { mapDataToTransaction } from '@/components/Transaction/utils';
import { getFileExtension, getTrans } from '@/utils/instantLoaded/tools';
import {
  ChatMsgType,
  HISTORY_TRANSIFY_KEY,
  OrderType,
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
  TRANSIFY_KEY,
  orderDialogueType,
  OrderCardType,
  OrderTypeUsingSmallOrder,
  Copywriting,
} from '@/utils/constants';
import {
  DIALOGUE_FROM, DIALOGUE_TYPE, INHOUSE_MESSAGE_TYPE, ButtonActionType,
  ServiceAssistantType,
} from '@/types/common';
import {
  HISTORY_MESSAGE_FROM,
  USER_SEND_TYPE,
  HISTORY_MESSAGE_TYPE,
  HistoryItem,
  UserSendHistory,
  ChatbotSendHistory,
  InhouseHistory,
  ParseInhouseHistory,
} from '@/types/history';
import { TransactionType } from '@/types/Transaction';
import { handleOrderRedirect } from './btnClickHandler';
import { UploadStatus } from '@/types/components/Video';
import { getNoticeTrackParams } from '@/components/Notice/utils/trackNotice';
import { ShowType, NoticeMsgType } from '@/components/Notice/types';
import { dispatchTrack } from './tracking';
import { UserEvents, UserEventStatus } from '@/stores/user/constants';
import { reportAgentMessage } from './reportAgentMessage';
import { COUNTRY } from '@/utils/locale';
import { cdnFile } from '@/utils/cdnFile';
import { OrderType as LiveChatOrderType } from '@/components/Order/OrderContent/FoodyOrderCard';

import initStore from '@/stores/init';
import userStore from '@/stores/user';
import { getConfig } from '@/config/core';
import { isDriverAndFoodUIOptimisation } from './experiment/getDriverAndFoodUIOptimisationExperiment';

// mock message_id 正常来说用不到
export const mockMessageId = () => Math.random().toString(36).substr(2);

// 匹配历史消息和消息来源
export const SENDER_DIALOGUE_MAPPING = {
  [HISTORY_MESSAGE_FROM.USER]: DIALOGUE_FROM.USER,
  [HISTORY_MESSAGE_FROM.CHATBOT]: DIALOGUE_FROM.BOT,
  [HISTORY_MESSAGE_FROM.AGENT]: DIALOGUE_FROM.AGENT,
};

// 使用mockId的reply type
const useMockIdType = (type: DIALOGUE_TYPE) => {
  const types = [DIALOGUE_TYPE.AgentTime, DIALOGUE_TYPE.DateTime];

  return types.indexOf(type) !== -1;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const handlerMapping = new Map([
  // user -> inhouse
  [
    `${HISTORY_MESSAGE_FROM.USER}_to_${HISTORY_MESSAGE_TYPE.IN_HOUSE}`,
    (item: HistoryItem) => agentHandler(item, DIALOGUE_FROM.USER),
  ],
  // inhouse -> user
  [
    `${HISTORY_MESSAGE_FROM.AGENT}_to_${HISTORY_MESSAGE_TYPE.IN_HOUSE}`,
    (item: HistoryItem) => agentHandler(item, DIALOGUE_FROM.AGENT),
  ],
  // user -> saleforce
  [
    `${HISTORY_MESSAGE_FROM.USER}_to_${HISTORY_MESSAGE_TYPE.SALE_FORCE}`,
    (item: HistoryItem) => agentHandler(item, DIALOGUE_FROM.USER),
  ],
  // saleforce -> user
  [
    `${HISTORY_MESSAGE_FROM.AGENT}_to_${HISTORY_MESSAGE_TYPE.SALE_FORCE}`,
    (item: HistoryItem) => agentHandler(item, DIALOGUE_FROM.AGENT),
  ],
  // user -> chatbot
  [
    `${HISTORY_MESSAGE_FROM.USER}_to_${HISTORY_MESSAGE_TYPE.CHATBOT}`,
    (item: HistoryItem) => userToChatbotHandler(item),
  ],
  // chatbot -> user
  [
    `${HISTORY_MESSAGE_FROM.CHATBOT}_to_${HISTORY_MESSAGE_TYPE.CHATBOT}`,
    (item: HistoryItem) => chatbotToUserHandler(item),
  ],
]);

export const historyHandler = (item: HistoryItem): any => {
  // 后端解析失败
  if (item.is_nonsupport) {
    return {
      ...unsupportedHandler(SENDER_DIALOGUE_MAPPING[item.sender]),
      message_id: item.message_id || mockMessageId(),
    };
  }

  let result: any = {};

  try {
    // eslint-disable-next-line @typescript-eslint/ban-types
    result = (handlerMapping.get(`${item.sender}_to_${item.msg_type}`) as Function)?.(item);
  } catch (e) {
    // 前端解析失败
    result = unsupportedHandler(DIALOGUE_FROM.BOT);
  }

  // 在taskbot 处理时返回空
  if (JSON.stringify(result) === '{}' || !result) {
    return;
  }

  // 可能返回值为数组。。
  // 如agent进线时。。
  if (Array.isArray(result)) {
    return result.map((msgItem) => ({
      ...msgItem,
      isHistory: true,
      historyDialogueId: item.dialogue_id,
      historySessionId: item.session_id,
      message_id: useMockIdType(msgItem.type) ? mockMessageId() : item.message_id || mockMessageId(),
    }));
  }

  return {
    ...result,
    isHistory: true,
    historyDialogueId: item.dialogue_id,
    historySessionId: item.session_id,
    message_id: item.message_id || mockMessageId(),
  };
};

export const userToChatbotHandler = (item: HistoryItem) => {
  const content = item.content as UserSendHistory;

  switch (content.type) {
    case USER_SEND_TYPE.TEXT:
      return {
        dialogueId: '',
        dialogueType: '',
        from: DIALOGUE_FROM.USER,
        type: DIALOGUE_TYPE.Msg,
        data: { content: content.text },
      };
    case USER_SEND_TYPE.ORDER_SELECT:
      const order = content?.order_select || {};
      const { order_type: orderType = OrderType.shopee } = order;
      const dialogueType = orderDialogueType[orderType as OrderType] || DIALOGUE_TYPE.Order;

      if (OrderTypeUsingSmallOrder.includes(orderType)) {
        order.orderCardType = OrderCardType.smallOrder;
      }

      return {
        dialogueId: '',
        dialogueType: '',
        from: DIALOGUE_FROM.USER,
        type: dialogueType,
        data: {
          ...order,
          image: cdnFile(order?.image),
          title: order?.name || order?.title,
          handleClick: () => {
            if (orderType === OrderType.food) {
              return;
            }

            initStore.UPDATE_DONT_SCROLL_TO_BOTTOM(true);
            const forbidRedirectCountries = ['co', 'cl'];
            if (forbidRedirectCountries.includes(COUNTRY) && order.uri_key?.includes('seller')) {
              return;
            }
            handleOrderRedirect({
              ...order,
              order_id: order.id || order.order_id,
            });
          },
          serviceType: order.service_type,
          isUIOptimisation: isDriverAndFoodUIOptimisation(),
        },
      };

    case USER_SEND_TYPE.TRANSACTION_SELECT_TOPUP:
      const { transaction_select_topup } = content;
      const topupProps = mapDataToTransaction(TransactionType.Topup, transaction_select_topup);
      return {
        dialogueId: '',
        dialogueType: '',
        from: DIALOGUE_FROM.USER,
        type: DIALOGUE_TYPE.Transaction,
        data: {
          ...topupProps,
          transReply: true,
          onClick: () => { },
        },
      };
    case USER_SEND_TYPE.TRANSACTION_SELECT_WITHDRAWL:
      const { transaction_select_withdrawl } = content;
      const withdrawlProps = mapDataToTransaction(TransactionType.Withdrawal, transaction_select_withdrawl);
      return {
        dialogueId: '',
        dialogueType: '',
        from: DIALOGUE_FROM.USER,
        type: DIALOGUE_TYPE.Transaction,
        data: {
          ...withdrawlProps,
          transReply: true,
          onClick: () => { },
        },
      };
    case USER_SEND_TYPE.CASE_SELECT:
      const { case_select } = content;

      return {
        dialogueId: '',
        dialogueType: '',
        from: DIALOGUE_FROM.USER,
        type: DIALOGUE_TYPE.EnquiryCaseCard,
        data: {
          hasOrderInfo: case_select.has_order_info,
          caseItem: {
            case_id: case_select.case_id,
            first_enquiry_time: case_select.first_enquiry_time,
            case_update_time: case_select.case_card_last_update_time,
            order_item_first_image_url: case_select.order_img,
            order_item_name: case_select.order_name,
            order_sn: case_select.order_sn,
            enquiry_type_name: case_select.enquiry_type_name,
          },
        },
      };
    default:
      return unsupportedHandler(DIALOGUE_FROM.USER);
  }
};

export const chatbotToUserHandler = (item: HistoryItem) => {
  const content = item.content as ChatbotSendHistory;
  // 对于部分组件，直接隐藏
  const WHITE_LIST = [REPLY_TYPE.DIRECTLY_REDIRECTION];
  let dialogueContent;

  if (WHITE_LIST.indexOf(content.reply_type) >= 0) {
    return;
  }

  try {
    dialogueContent = RESPONSE_HANDLER[content.reply_type]?.(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      { dialogue_id: '', dialogue_type: '', dialogue: content, sendTime: item.send_time },
      null,
      true,
    );
  } catch (e) { }

  if (dialogueContent) {
    // 判断是否需要展示新版feedback
    const { feedback_info, buttons_for_feedback } = content.message_extra;
    if (dialogueContent && feedback_info) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dialogueContent.feedbackInfo = {
        ...feedback_info,
        options: buttons_for_feedback,
      };
    }

    return {
      ...dialogueContent,
      isHistory: true,
    };
  }

  if (!dialogueContent && RESPONSE_HANDLER[content.reply_type]) {
    return;
  }

  return unsupportedHandler(DIALOGUE_FROM.BOT);
};

export const agentHandler = (item: HistoryItem, from: DIALOGUE_FROM = DIALOGUE_FROM.AGENT) => {
  const content = (item.content as InhouseHistory).content;

  if (from !== DIALOGUE_FROM.USER) {
    reportAgentMessage(item.message_id);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return GetChatMsgFuncsNext({ ...item, content: { ...item?.content, content: { ...content }}}, from);
};

export const unsupportedHandler = (from: DIALOGUE_FROM) => {
  return {
    isHistory: true,
    dialogueId: '',
    dialogueType: '',
    from: from,
    type: DIALOGUE_TYPE.Msg,
    data: { content: getTrans(HISTORY_TRANSIFY_KEY.UNSUPPORTED_MESSAGE) },
  };
};

const GetChatMsgFuncsNext = (messageItem: HistoryItem, from: DIALOGUE_FROM) => {
  const sendTime = messageItem.send_time;
  const content = messageItem.content as ParseInhouseHistory;
  const {
    cs_name: name,
    is_transfer,
    notice_msg,
    buttons = [],
    variables,
  } = content.content;

  switch (content.msg_type) {
    case INHOUSE_MESSAGE_TYPE.CS_JOINED:

      if (is_transfer) {
        return {
          from: DIALOGUE_FROM.SYSTEM,
          type: DIALOGUE_TYPE.SessionTransfer,
          data: { agentName: name },
        };
      }

      return [
        {
          from: DIALOGUE_FROM.SYSTEM,
          type: DIALOGUE_TYPE.AgentEnter,
          data: { name },
        },
        {
          from: DIALOGUE_FROM.SYSTEM,
          type: DIALOGUE_TYPE.AgentTime,
          data: { time: sendTime },
        },
      ];
    case INHOUSE_MESSAGE_TYPE.TEXT:
      const { text = '' } = content.content;

      return {
        from,
        type: DIALOGUE_TYPE.Msg,
        data: {
          content: text,
          from,
        },
      };
    case INHOUSE_MESSAGE_TYPE.FILE:
      const { size, name: title = '', url, text: fileText } = content.content;

      return {
        from,
        type: DIALOGUE_TYPE.FileUpload,
        data: {
          title,
          size,
          type: getFileExtension(title),
          loading: false,
          fileUrl: url || fileText,
        },
      };
    case INHOUSE_MESSAGE_TYPE.IMG:
      const { url: imageUrl, thumbnail } = content.content;

      return {
        from,
        type: DIALOGUE_TYPE.HistoryImage,
        data: {
          url: imageUrl,
          thumbnail,
        },
      };
    case INHOUSE_MESSAGE_TYPE.VID:
      const { url: videoUrl, thumbnail: videoCoverUrl, duration: videoDuration } = content.content;

      return {
        from,
        type: DIALOGUE_TYPE.Video,
        data: {
          videoUrl: videoUrl,
          videoCoverUrl: videoCoverUrl,
          videoDuration: videoDuration,
          status: UploadStatus.SendMessageSucceed,
        },
      };
    case INHOUSE_MESSAGE_TYPE.ORDER:
      const order_select = content.content;
      const { order_type } = order_select as any;
      return {
        from,
        type: DIALOGUE_TYPE.Order,
        data: {
          orderCardType: (order_type && order_type !== LiveChatOrderType.Shopee) ? OrderCardType.newCarousel : OrderCardType.oldCarousel,
          ...order_select,
          title: order_select?.order_name,
          handleClick: () => {
            initStore.UPDATE_DONT_SCROLL_TO_BOTTOM(true);
            const forbidRedirectCountries = ['co', 'cl'];
            if (forbidRedirectCountries.includes(COUNTRY) && order_select?.uri_key?.includes('seller')) {
              return;
            }
            handleOrderRedirect({
              ...(order_select as any),
              title: order_select?.order_name,
            });
          },
        },
      };
    case INHOUSE_MESSAGE_TYPE.CHAT_ENDED:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.AgentLeave,
        data: { name: content.content.cs_name },
      };
    case INHOUSE_MESSAGE_TYPE.SESSION_CLOSE_TIMEOUT:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.SessionTimeout,
        data: {},
      };
    case INHOUSE_MESSAGE_TYPE.USER_CLOSE_SESSION:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.UserCloseSession,
        data: {},
      };
    case INHOUSE_MESSAGE_TYPE.CHASING_TIMEOUT_CLOSE_SESSION:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.UserCloseSession,
        data: { content: getTrans('chat_chasing_timeout_close_session') },
      };
    case INHOUSE_MESSAGE_TYPE.CHASING_USER_CLOSE_SESSION:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.UserCloseSession,
        data: {},
      };
    case INHOUSE_MESSAGE_TYPE.CS_CLOSE_SESSION:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.AgentLeave,
        data: { name: content.content.cs_name },
      };
    case INHOUSE_MESSAGE_TYPE.QUEUING_CHECK_NOTICE:
      return {
        from: DIALOGUE_FROM.AGENT,
        type: DIALOGUE_TYPE.CustomOptions,
        data: {
          type: 'horizontal',
          title: getTrans(TRANSIFY_KEY.QUEUE_FILTER_MESSAGE),
          options: [
            { content: getTrans('btn_feedback_2') },
            { content: getTrans('btn_feedback_1') },
          ],
        },
      };
    case INHOUSE_MESSAGE_TYPE.SYSTEM_NOTICE:
      return {
        from: DIALOGUE_FROM.AGENT,
        type: DIALOGUE_TYPE.SystemNotice,
        data: {
          title: notice_msg,
          variables: variables,
          buttons: buttons.map(o => {
            return {
              buttonType: o.button_type,
              content: o.button_msg,
              variables: o.variables,
              url: o.url,
              replyMsg: o.reply_msg,
              actionType: o.action_type || ButtonActionType.None,
            };
          }),
        },
      };
    case INHOUSE_MESSAGE_TYPE.SESSION_END:
    case ChatMsgType.AGENT_DISCONNECT:
    case ChatMsgType.CHAT_REQUEST_FAIL:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.SessionEnd,
        data: {},
      };
    case ChatMsgType.CHAT_TRANSFER:
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.ChatTransfer,
        data: {},
      };
    case INHOUSE_MESSAGE_TYPE.SERVICE_ASSISTANT: {
      const { type, countdown = '' } = content.content;
      const btns = [
        { content: getTrans('message_liveagent_chatwithagent') },
        { content: getTrans('chat_send_us_a_enquiry') },
      ];
      const config = getConfig();
      const { webFormConfig } = config;
      // bank不支持webform，去除webform的按钮
      if (!webFormConfig?.hasWebform) {
        btns.pop();
      }

      const messageMap: Record<string, unknown> = {
        [ServiceAssistantType.ChasingStart]: {
          hasHeader: true,
          title: getTrans('chat_chasing_start_msg_y_second', '', { Y: countdown }),
          type: NoticeMsgType.ChasingStart,
          align: ShowType.Horizontal,
          impressionTracker: getNoticeTrackParams({ trackingname: 'ChasingUserMessage' }),
          buttons: [{ content: getTrans('chat_no_end_chat') }, { content: getTrans('chat_yes_continue') }],
        },
        [ServiceAssistantType.IncompleteStart]: {
          hasHeader: true,
          title: getTrans('chat_incomplete_chasing_start_msg', '', { Y: countdown }),
          type: NoticeMsgType.ChasingStart,
          align: ShowType.Horizontal,
          impressionTracker: getNoticeTrackParams({ trackingname: 'ChasingUserMessage' }),
          buttons: [{ content: getTrans('chat_no_end_chat') }, { content: getTrans('chat_yes_continue') }],
        },
        [ServiceAssistantType.ChasingEnd]: {
          hasHeader: true,
          title: getTrans('chat_chasing_end_msg'),
          type: NoticeMsgType.ChasingEnd,
          impressionTracker: getNoticeTrackParams({ trackingname: 'ChasingAutoEndMessage' }),
          buttons: btns,
        },
        [ServiceAssistantType.IncompleteEnd]: {
          hasHeader: true,
          title: getTrans('chat_incomplete_chasing_end_msg'),
          type: NoticeMsgType.ChasingEnd,
          impressionTracker: getNoticeTrackParams({ trackingname: 'ChasingAutoEndMessage' }),
          buttons: btns,
        },
      };
      if (!type || !messageMap[type]) {
        // 不支持的消息
        break;
      }
      return ({
        from: DIALOGUE_FROM.AGENT,
        type: DIALOGUE_TYPE.SystemNotice,
        data: messageMap[type],
      });
    }

    case INHOUSE_MESSAGE_TYPE.FACIAL_VERIFICATION: {
      return {
        from: DIALOGUE_FROM.AGENT,
        type: DIALOGUE_TYPE.FacialVerification,
        data: {},
      };
    }
    case INHOUSE_MESSAGE_TYPE.FACIAL_VERIFICATION_FAILED: {
      const failedText = getTrans(Copywriting.FACIAL_VERIFICATION_FAILED_TIP);
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.AgentTip,
        data: { content: failedText },
      };
    }
    case INHOUSE_MESSAGE_TYPE.FACIAL_VERIFICATION_SUCCESS: {
      const successText = getTrans(Copywriting.FACIAL_VERIFICATION_SUCCESS_TIP);
      return {
        from: DIALOGUE_FROM.SYSTEM,
        type: DIALOGUE_TYPE.AgentTip,
        data: { content: successText },
      };
    }

    default:
      return unsupportedHandler(from || DIALOGUE_FROM.AGENT);
  }
};

export function trackStartLoadHistory() {
  const hasHistoryPrompt = userStore.userEvents[UserEvents.GetHistoryPrompt] === UserEventStatus.NonExist;
  const trackingName = hasHistoryPrompt ? TRACKING_TYPE.SCROLL_DOWN_TO_VIEW_HISTORY : TRACKING_TYPE.LOADING_HISTORY;

  dispatchTrack({
    operation: TRACKING_OPERATION.ACTION_START_LOAD_HISTORY,
    target: TRACKING_TARGET[trackingName],
    section: TRACKING_SECTION[trackingName],
    data: {
      current_chat_session_id: initStore.sessionId || '0',
      client_rank: userStore?.loyaltyTierCode?.toString() || '',
    },
  });
}

export function trackFinishLoadHistory() {
  const hasHistoryPrompt = userStore.userEvents[UserEvents.GetHistoryPrompt] === UserEventStatus.NonExist;
  const trackingName = hasHistoryPrompt ? TRACKING_TYPE.SCROLL_DOWN_TO_VIEW_HISTORY : TRACKING_TYPE.LOADING_HISTORY;

  dispatchTrack({
    operation: TRACKING_OPERATION.ACTION_FINISH_LOAD_HISTORY,
    target: TRACKING_TARGET[trackingName],
    section: TRACKING_SECTION[trackingName],
    data: {
      current_chat_session_id: initStore.sessionId || '0',
      client_rank: userStore?.loyaltyTierCode?.toString() || '',
    },
  });
}
